import { assertNever } from '@deltasierra/shared';
import { SelectProps, Checkbox, InputLabel, ListItemText, MenuItem, Select } from '@material-ui/core';
import * as React from 'react';
import styled from 'styled-components';
import { DSFormControl } from '../../../mui';

const StyledFormControl = styled(DSFormControl)`
    min-width: 120px;
`;

type MultiselectWidthType = 'medium' | 'small';

function widthToPx(width: MultiselectWidthType): number {
    switch (width) {
        case 'medium':
            return 250;
        case 'small':
            return 150;
        default:
            return assertNever(width);
    }
}

type Unpacked<T> =
    T extends Array<infer U> ? U : T extends (...args: any[]) => infer U ? U : T extends Promise<infer U> ? U : T;

export type MultiselectProps<T extends unknown[] = string[]> = Pick<SelectProps, 'classes' | 'className' | 'style'> &
    (Unpacked<T> extends string
        ? {
              mapper?: (value: T[number]) => string;
          }
        : {
              mapper: (value: T[number]) => string;
          }) & {
        fullWidth?: boolean;
        label?: React.ReactNode;
        menuWidth?: MultiselectWidthType;
        options: T;
        placeholder?: string;
        values: T;
        onChange: (selected: T) => void;
    };

export function Multiselect<T extends unknown[]>({
    fullWidth,
    label,
    mapper = (value: unknown) => value as string,
    menuWidth = 'medium',
    onChange,
    options: menuOptions,
    placeholder,
    values,
    ...props
}: MultiselectProps<T>): JSX.Element {
    const handleChangeMultiple = (event: React.ChangeEvent<{ value: unknown }>) => {
        let allValues = event.target.value as string[];
        if (placeholder) {
            allValues = allValues.filter(value => value !== placeholder);
        }
        onChange(menuOptions.filter(option => allValues.includes(mapper(option))) as T);
    };
    const menuWidthPx = widthToPx(menuWidth);

    return (
        <StyledFormControl fullWidth={fullWidth} margin="dense" variant="outlined">
            {label && <InputLabel>{label}</InputLabel>}
            <Select
                MenuProps={{
                    PaperProps: {
                        style: {
                            maxWidth: menuWidthPx,
                            minWidth: menuWidthPx,
                        },
                    },
                    anchorOrigin: {
                        horizontal: 'left',
                        vertical: 'bottom',
                    },
                    getContentAnchorEl: null,
                    transformOrigin: {
                        horizontal: 'left',
                        vertical: 'top',
                    },
                }}
                label={label}
                multiple
                renderValue={selected => {
                    const items = selected as string[];
                    return items.join(', ');
                }}
                value={!!values.length || !placeholder ? values.map(mapper) : [placeholder]}
                onChange={handleChangeMultiple}
                {...props}
            >
                {menuOptions.map(option => (
                    <MenuItem key={mapper(option)} value={mapper(option)}>
                        <Checkbox checked={values.includes(option)} />
                        <ListItemText primary={mapper(option)} />
                    </MenuItem>
                ))}
            </Select>
        </StyledFormControl>
    );
}
Multiselect.displayName = 'Multiselect';
